import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const DomainContext = createContext();

const PLAN_LIMITS = {
  free: 1,
  trial: 1,
  individual: 1,
  business: 3,
  enterprise: 10
};

export const DomainProvider = ({ children }) => {
  const { user } = useAuth();
  const fetchingRef = useRef(false);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [domainLimit, setDomainLimit] = useState(1);

  const fetchDomains = useCallback(async () => {
    if (!user) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/domains', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.domains || [];
      setDomains(list);
      const plan = (user.plan || 'free').toLowerCase();
      setDomainLimit(PLAN_LIMITS[plan] || 1);
      // Auto-select first domain if none selected
      if (!selectedDomain && list.length > 0) {
        const saved = localStorage.getItem('selectedDomain');
        const match = list.find(d => d.domain === saved);
        setSelectedDomain(match ? match.domain : list[0].domain);
      }
    } catch (err) {
      console.error('Failed to fetch domains:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const selectDomain = (domain) => {
    setSelectedDomain(domain);
    localStorage.setItem('selectedDomain', domain);
  };

  const canAddDomain = domains.length < domainLimit;

  const addDomain = async (domain) => {
    if (!canAddDomain) {
      return { success: false, error: `Your plan allows up to ${domainLimit} domain(s). Please upgrade.` };
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/domains', { domain }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.domain) {
        await fetchDomains();
        selectDomain(domain.toLowerCase().trim());
        return { success: true };
      }
      return { success: false, error: res.data.message || 'Failed to add domain' };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || 'Failed to add domain' };
    }
  };

  return (
    <DomainContext.Provider value={{
      domains,
      selectedDomain,
      selectDomain,
      addDomain,
      canAddDomain,
      domainLimit,
      loading,
      fetchDomains
    }}>
      {children}
    </DomainContext.Provider>
  );
};

export const useDomain = () => {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error('useDomain must be used within a DomainProvider');
  }
  return context;
};

export default DomainContext;
